// client/src/pages/Couriers/CourierProfile.jsx
import {
    Avatar,
    Badge,
    Box,
    Button,
    Divider,
    Heading,
    HStack,
    Icon,
    SimpleGrid,
    Text,
    VStack,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { MdCarRental, MdPerson, MdSmartphone, MdStorage } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import { fetchCourierWeekSchedule, getCourierById } from "../../api";

export default function CourierProfile() {
    const navigate = useNavigate();
    const { id } = useParams();
    const scheduleRef = useRef();

    const [courier, setCourier] = useState(null);
    const [weeklySchedule, setWeeklySchedule] = useState([]); // [{date, startTime, dayOff, routeId:{name}}]
    const [showDeviceInfo, setShowDeviceInfo] = useState(false);
    const [tapCount, setTapCount] = useState(0);

    // Monday as week start (matches your existing logic)
    const weekStart = useMemo(() => dayjs().startOf("week").add(1, "day"), []);
    const weekStartKey = useMemo(() => weekStart.format("YYYY-MM-DD"), [weekStart]);

    useEffect(() => {
        let mounted = true;

        (async () => {
            try {
                const res = await getCourierById(id);
                if (mounted && res?.success) setCourier(res.data);

                const scheduleRes = await fetchCourierWeekSchedule(id, weekStartKey);

                console.log(scheduleRes)
                if (mounted && scheduleRes?.success) setWeeklySchedule(scheduleRes.data || []);
            } catch (e) {
                // keep minimal; your app likely has global error handling
                console.error(e);
            }
        })();

        return () => {
            mounted = false;
        };
    }, [id, weekStartKey]);

    if (!courier) return <Text>Loading...</Text>;

    const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

    const todayBadgeText = (() => {
        if (!courier.isWorkingToday) return "Not Scheduled for today";
        const routeName =
            courier?.scheduleRouteId?.name ||
            courier?.scheduleRouteName ||
            courier?.route?.name ||
            "";
        const time = courier?.scheduleStartTime ? ` @ ${courier.scheduleStartTime}` : "";
        return `On Schedule For Today${routeName ? " • " + routeName : ""}${time}`;
    })();

    return (
        <Box>
            <HStack spacing={6} mb={8}>
                <Avatar
                    size="xl"
                    name={courier.name}
                    onClick={() => {
                        setTapCount((prev) => {
                            const next = prev + 1;
                            if (next >= 10) {
                                setShowDeviceInfo((s) => !s);
                                return 0;
                            }
                            setTimeout(() => setTapCount(0), 2000);
                            return next;
                        });
                    }}
                />
                <VStack align="start">
                    <Heading size="lg">{courier.name}</Heading>

                    <Badge colorScheme={courier.isWorkingToday ? "green" : "red"}>{todayBadgeText}</Badge>

                    <Text fontSize="sm" color="gray.500">
                        Courier ID: {courier._id}
                    </Text>
                </VStack>
            </HStack>

            <SimpleGrid templateColumns={{ base: "1fr", md: "3fr 1fr" }} spacing={8} p={8}>
                <Box>
                    <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                        <Heading size="md" mb={4}>
                            <Icon as={MdPerson} mr={2} />
                            Personal Info
                        </Heading>
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                            <Text>
                                <b>Email:</b> {courier.email}
                            </Text>
                            <Text>
                                <b>Phone:</b> {courier.phone}
                            </Text>
                            <Text>
                                <b>Hire Date:</b> {courier.hireDate ? new Date(courier.hireDate).toLocaleDateString() : "—"}
                            </Text>
                            <Text>
                                <b>Date of Birth:</b> {courier.dob ? new Date(courier.dob).toLocaleDateString() : "—"}
                            </Text>
                        </SimpleGrid>
                    </Box>

                    <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                        <Heading size="md" mb={4}>
                            Driver License
                        </Heading>
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                            <Text>
                                <b>License #:</b> {courier?.dl?.licenseNumber || "—"}
                            </Text>
                            <Text>
                                <b>Expiry:</b>{" "}
                                {courier?.dl?.licenseExpiry ? new Date(courier.dl.licenseExpiry).toLocaleDateString() : "—"}
                            </Text>
                            <Text>
                                <b>Address:</b> {courier?.dl?.address || "—"}
                            </Text>
                            <Text>
                                <b>State:</b> {courier?.dl?.state || "—"}
                            </Text>
                            <Text>
                                <b>Class:</b> {courier?.dl?.class || "—"}
                            </Text>
                        </SimpleGrid>
                    </Box>

                    <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                        <Heading size="md" mb={4}>
                            <Icon as={MdCarRental} mr={2} />
                            Car Info
                        </Heading>
                        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                            <Text>
                                <b>Make:</b> {courier?.car?.make || "—"}
                            </Text>
                            <Text>
                                <b>Model:</b> {courier?.car?.model || "—"}
                            </Text>
                            <Text>
                                <b>Color:</b> {courier?.car?.color || "—"}
                            </Text>
                            <Text>
                                <b>Year:</b> {courier?.car?.year || "—"}
                            </Text>
                            <Text>
                                <b>Plate:</b> {courier?.car?.licensePlate || "—"}
                            </Text>
                        </SimpleGrid>
                    </Box>

                    {showDeviceInfo && (
                        <Box mb={8} p={6} borderWidth="1px" borderRadius="lg">
                            <Heading size="md" mb={4}>
                                <Icon as={MdSmartphone} mr={2} />
                                Device Info
                            </Heading>
                            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                                <Text>
                                    <b>Device:</b> {courier.appInfo?.appDetails?.deviceName} ({courier.appInfo?.appDetails?.model})
                                </Text>
                                <Text>
                                    <b>Device ID:</b> {courier.appInfo?.appDetails?.deviceId}
                                </Text>
                                <Text>
                                    <b>System:</b> {courier.appInfo?.appDetails?.systemName} {courier.appInfo?.appDetails?.systemVersion}
                                </Text>
                                <Text>
                                    <b>Brand:</b> {courier.appInfo?.appDetails?.brand}
                                </Text>
                                <Text>
                                    <b>Manufacturer:</b> {courier.appInfo?.appDetails?.manufacturer}
                                </Text>
                                <Text>
                                    <b>Type:</b> {courier.appInfo?.appDetails?.deviceType}
                                </Text>
                                <Text>
                                    <b>Emulator:</b> {courier.appInfo?.appDetails?.isEmulator ? "Yes" : "No"}
                                </Text>
                                <Text>
                                    <b>Tablet:</b> {courier.appInfo?.appDetails?.isTablet ? "Yes" : "No"}
                                </Text>
                                <Text>
                                    <b>Carrier:</b> {courier.appInfo?.appDetails?.carrier || "Unknown"}
                                </Text>
                                <Text>
                                    <b>Push Token:</b> {String(courier.appInfo?.pushNotification || "")}
                                </Text>
                                <Text>
                                    <b>Unique ID:</b> {courier.appInfo?.appDetails?.uniqueId}
                                </Text>
                            </SimpleGrid>

                            <Divider my={4} />

                            <Heading size="sm" mb={2}>
                                <Icon as={MdStorage} mr={2} />
                                Storage & Memory
                            </Heading>
                            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                                <Text>
                                    <b>Total Memory:</b>{" "}
                                    {courier.appInfo?.appDetails?.totalMemory
                                        ? (courier.appInfo.appDetails.totalMemory / 1e9).toFixed(2)
                                        : "—"}{" "}
                                    GB
                                </Text>
                                <Text>
                                    <b>Used Memory:</b>{" "}
                                    {courier.appInfo?.appDetails?.usedMemory
                                        ? (courier.appInfo.appDetails.usedMemory / 1e6).toFixed(0)
                                        : "—"}{" "}
                                    MB
                                </Text>
                                <Text>
                                    <b>Free Disk:</b>{" "}
                                    {courier.appInfo?.appDetails?.freeDiskStorage
                                        ? (courier.appInfo.appDetails.freeDiskStorage / 1e9).toFixed(2)
                                        : "—"}{" "}
                                    GB
                                </Text>
                                <Text>
                                    <b>Total Disk:</b>{" "}
                                    {courier.appInfo?.appDetails?.totalDiskCapacity
                                        ? (courier.appInfo.appDetails.totalDiskCapacity / 1e9).toFixed(2)
                                        : "—"}{" "}
                                    GB
                                </Text>
                            </SimpleGrid>
                        </Box>
                    )}

                    <Button colorScheme="blue" onClick={() => navigate(-1)}>
                        ← Back
                    </Button>
                </Box>

                <Box mb="19%" ref={scheduleRef} p={6} borderWidth="1px" borderRadius="lg">
                    <Button
                        colorScheme="gray"
                        size="sm"
                        mb={4}
                        onClick={() => {
                            const rows = days
                                .map((day, i) => {
                                    const dateKey = weekStart.add(i, "day").format("YYYY-MM-DD");
                                    const dayData = weeklySchedule.find((s) => s.date === dateKey);
                                    const isOff = !dayData || dayData.dayOff === true;

                                    const route = isOff ? "Day Off" : (dayData?.routeId?.name || "—");
                                    const startTime =
                                        !isOff && dayData?.startTime
                                            ? dayjs(`1970-01-01T${dayData.startTime}`).format("h:mm A")
                                            : "—";

                                    const dayDate = weekStart.add(i, "day").format("MMM D, YYYY");
                                    const tdClass = isOff ? "day-off" : "";

                                    return `
        <tr>
          <td>${day} <small>(${dayDate})</small></td>
          <td class="${tdClass}">${route}</td>
          <td>${startTime}</td>
        </tr>
      `;
                                })
                                .join("");

                            const newWindow = window.open("", "", "width=800,height=600");
                            if (!newWindow) return;

                            newWindow.document.write(`
    <html>
      <head>
        <title>Schedule for ${courier.name}</title>
        <style>
          body { font-family: tahoma; padding: 40px; }
          h1 { margin-bottom: 24px; text-align: center; }
          table { width: 100%; border-collapse: collapse; margin-top: 16px; }
          thead { background-color: #f0f0f0; }
          th, td { padding: 12px; border: 2px solid #ccc; text-align: center; }
          th { font-weight: bold; }
          .day-off { color: #888; font-style: italic; }
        </style>
      </head>
      <body>
        <h1>Weekly Schedule – ${courier.name}</h1>
        <table>
          <thead>
            <tr><th>Day</th><th>Route</th><th>Start Time</th></tr>
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>
      </body>
    </html>
  `);

                            newWindow.document.close();
                            newWindow.focus();
                            newWindow.print();
                            newWindow.close();
                        }}
                    >
                        Print Schedule
                    </Button>

                    <Heading size="md" mb={4}>
                        Weekly Schedule
                    </Heading>

                    <Box>
                        {days.map((day, i) => {
                            const dateKey = weekStart.add(i, "day").format("YYYY-MM-DD");
                            const dayData = weeklySchedule.find((s) => s.date === dateKey);
                            const isToday = day === dayjs().format("dddd");
                            const isOff = !dayData || dayData.dayOff === true;

                            return (
                                <Box key={day} p={4} mt={5} borderWidth="1px" borderRadius="md">
                                    <Heading size="sm" mb={2}>
                                        {day}{" "}
                                        <small>({weekStart.add(i, "day").format("MMM D, YYYY")})</small>
                                        {isToday && (
                                            <Badge ml={3} colorScheme="green">
                                                Today
                                            </Badge>
                                        )}
                                    </Heading>

                                    {isOff ? (
                                        <Badge colorScheme="red">Day Off</Badge>
                                    ) : (
                                        <>
                                            <Text>
                                                <b>Route:</b> {dayData?.routeId?.name || "—"}
                                            </Text>
                                            <Text>
                                                <b>Time:</b>{" "}
                                                {dayData?.startTime
                                                    ? dayjs(`1970-01-01T${String(dayData.startTime).trim()}`).format("h:mm A")
                                                    : "—"}
                                            </Text>
                                        </>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </SimpleGrid>
        </Box>
    );
}